import React, { useEffect, useState } from "react";
import { Mail, Phone, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import StatusBadge from "../components/StatusBadge";
import { useToast } from "../hooks/use-toast";
import { faqs } from "../mock";
import api, { errMsg } from "../api";

function Card({ children, className = "", ...rest }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`} {...rest}>{children}</div>;
}

function Contacts({ cfg }) {
  const items = [
    { key: "email", label: "Email", value: cfg.email, Icon: Mail, href: cfg.email ? `mailto:${cfg.email}` : null },
    { key: "phone", label: "Phone", value: cfg.phone, Icon: Phone, href: cfg.phone ? `tel:${cfg.phone.replace(/[^\d+]/g, "")}` : null },
    { key: "hours", label: "Support hours", value: cfg.hours, Icon: Clock },
  ].filter((c) => c.value);
  if (!items.length) {
    return <Card data-testid="support-contacts-empty"><p className="text-sm text-slate-500">Support contact details haven't been published yet. Use the form below — your request goes straight to the Campbell Communications team.</p></Card>;
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {items.map((c) => (
        <Card key={c.key} data-testid={`support-contact-${c.key}`}>
          <c.Icon className="h-5 w-5 text-violet-600" />
          <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{c.label}</div>
          {c.href ? <a href={c.href} className="mt-1 block text-sm font-medium text-violet-700 hover:underline">{c.value}</a> : <div className="mt-1 text-sm font-medium text-slate-800">{c.value}</div>}
        </Card>
      ))}
    </div>
  );
}

export default function Support() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState({});
  const [subject, setSubject] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mine, setMine] = useState([]);

  const loadMine = () => api.get("/support/requests").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { api.get("/support/config").then((r) => setCfg(r.data)).catch(() => {}); loadMine(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!subject.trim()) { setError("Subject is required."); return; }
    setBusy(true);
    try {
      const r = await api.post("/support/requests", { subject, details });
      toast({
        title: "Request submitted",
        description: r.data.team_emailed ? "The support team has been emailed." : "Saved — the team will see it in the admin portal.",
      });
      setSubject(""); setDetails("");
      loadMine();
    } catch (err) {
      setError(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <Contacts cfg={cfg} />

      <Card>
        <h2 className="text-base font-bold text-slate-900">Submit a support request</h2>
        {error && <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="support-error">{error}</div>}
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="subject">Subject *</Label>
            <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary of your issue" data-testid="support-subject-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="details">Details</Label>
            <Textarea id="details" rows={5} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Describe what you need help with…" data-testid="support-details-input" />
          </div>
          <Button type="submit" disabled={busy} data-testid="support-submit-btn" className="btn-primary-gradient border-0 text-white">{busy ? "Submitting…" : "Submit request"}</Button>
        </form>
      </Card>

      <Card data-testid="my-support-requests">
        <h2 className="text-base font-bold text-slate-900">My requests</h2>
        <div className="mt-3 space-y-2">
          {mine.length === 0 && <div className="text-sm text-slate-400">You haven't submitted any requests.</div>}
          {mine.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-100 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-800">{r.subject}</div>
                <StatusBadge status={r.status} />
              </div>
              <div className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</div>
              {r.reply && <div className="mt-2 rounded-lg bg-violet-50 px-3 py-2 text-xs text-violet-800">Team reply: {r.reply}</div>}
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <h2 className="text-base font-bold text-slate-900">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="mt-2">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left text-sm font-medium text-slate-800" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-500">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Card>
    </div>
  );
}
